import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import type React from "react";
import { MainMenu } from "@excalidraw/excalidraw";
import { useProfilePhoto } from "../hooks/useProfilePhoto";
import type { AccountInfo } from "@azure/msal-browser";
import type { SceneMode } from "@excalidraw-ms/shared";
import type { ThemePreference } from "../hooks/useThemePreference";
import AppearanceSelector from "./AppearanceSelector";

interface CustomMainMenuProps {
  account?: AccountInfo | null;
  /** Current scene mode — controls which native Excalidraw items are visible (rules 20–26). */
  sceneMode?: SceneMode;
  /** Whether live collaboration is active — gates Import/LoadScene (rule 23). */
  isCollaborating?: boolean;
  /** Whether backend-backed menu actions may be offered. */
  backendFeaturesEnabled?: boolean;
  /** Keep backend actions visible but inert during a retryable service failure. */
  backendFeaturesUnavailable?: boolean;
  /** Confirmed access denial, not an access check or a temporary outage. */
  isLocalOnly?: boolean;
  onGetFullAccess?: () => void;
  onOpenLiveCollab?: () => void;
  /**
   * Open the shared Create with AI chooser (ux-rules § 15g).
   */
  onConnectAi?: () => void;
  onSave?: () => void;
  saveDisabled?: boolean;
  onNewDrawing?: () => void;
  onSignOut?: () => void;
  onAbout?: () => void;
  onFeedback?: () => void;
  onDevTools?: () => void;
  getPreferenceState?: () => SupportedPreferenceState;
  onTogglePreference?: (preference: SupportedPreference) => SupportedPreferenceState;
  preferencesEditable?: boolean;
  themePreference?: ThemePreference;
  onThemePreferenceChange?: (theme: ThemePreference) => void;
}

export type SupportedPreference =
  | "toolLock"
  | "objectsSnapMode"
  | "gridMode"
  | "zenMode"
  | "viewMode"
  | "canvasProperties";

export interface SupportedPreferenceState {
  toolLock: boolean;
  objectsSnapMode: boolean;
  gridMode: boolean;
  zenMode: boolean;
  viewMode: boolean;
  canvasProperties: boolean;
}

/**
 * Custom <MainMenu> child component for the Excalidraw editor.
 *
 * Groups: Drawing, conditional Sharing, Canvas & editing, Help, Account.
 */
export default function CustomMainMenu({
  account,
  sceneMode = "scene-only",
  isCollaborating = false,
  backendFeaturesEnabled = true,
  backendFeaturesUnavailable = false,
  isLocalOnly = false,
  onGetFullAccess,
  onOpenLiveCollab,
  onConnectAi,
  onSave,
  saveDisabled = false,
  onNewDrawing,
  onSignOut,
  onAbout,
  onFeedback,
  onDevTools,
  getPreferenceState,
  onTogglePreference,
  preferencesEditable = true,
  themePreference = "system",
  onThemePreferenceChange,
}: CustomMainMenuProps) {
  const photoUrl = useProfilePhoto();
  const displayName = account?.name ?? account?.username ?? "User";
  const initials = getInitials(displayName);
  const isSceneOnly = sceneMode === "scene-only";
  // Import/Open only available in scene-only solo mode (rule 23–24)
  const showImport = isSceneOnly && !isCollaborating;

  return (
    <MainMenu>
      {/* --- Drawing --- */}
      {onNewDrawing && (
        <MainMenu.Item onSelect={onNewDrawing} icon={newDrawingIcon}>
          New drawing
        </MainMenu.Item>
      )}
      {!isSceneOnly && onSave && (
        <MainMenu.Item onSelect={onSave} icon={saveIcon} shortcut="Ctrl+S" disabled={saveDisabled}>
          Save
        </MainMenu.Item>
      )}
      {showImport && <MainMenu.DefaultItems.LoadScene />}
      <MainMenu.DefaultItems.Export />
      <MainMenu.DefaultItems.SaveAsImage />

      {(backendFeaturesEnabled || backendFeaturesUnavailable) &&
        isCollaborating &&
        onOpenLiveCollab && (
          <>
            <MainMenu.Separator />
            <MainMenu.Item
              onSelect={onOpenLiveCollab}
              icon={liveCollabIcon}
              style={liveCollabItemStyle}
              disabled={backendFeaturesUnavailable}
            >
              Live collaboration...
            </MainMenu.Item>
          </>
        )}

      <MainMenu.Separator />

      {/* --- Canvas & editing --- */}
      <MainMenu.DefaultItems.CommandPalette />
      <MainMenu.DefaultItems.SearchMenu />
      {getPreferenceState && onTogglePreference && (
        <PreferencesMenu
          getPreferenceState={getPreferenceState}
          onTogglePreference={onTogglePreference}
          editable={preferencesEditable}
        />
      )}
      <MainMenu.DefaultItems.ChangeCanvasBackground />
      <MainMenu.DefaultItems.ClearCanvas />

      <MainMenu.Separator />

      <MainMenu.DefaultItems.Help />
      {onConnectAi && (
        <MainMenu.Item onSelect={onConnectAi} icon={createAiIcon}>
          Create with AI
        </MainMenu.Item>
      )}
      {onFeedback && (
        <MainMenu.Item onSelect={onFeedback} icon={feedbackIcon}>
          Feedback
        </MainMenu.Item>
      )}
      {onAbout && (
        <MainMenu.Item onSelect={onAbout} icon={aboutIcon}>
          About
        </MainMenu.Item>
      )}
      {onDevTools && (
        <MainMenu.Item onSelect={onDevTools} icon={devToolsIcon}>
          Dev tools
        </MainMenu.Item>
      )}

      <MainMenu.Separator />

      {/* --- User identity --- */}
      {account && (
        <MainMenu.ItemCustom style={profileItemStyle}>
          {photoUrl ? (
            <img
              src={photoUrl}
              alt={displayName}
              style={menuAvatarStyle}
              referrerPolicy="no-referrer"
            />
          ) : (
            <span style={menuInitialsStyle}>{initials}</span>
          )}
          <span style={menuNameStyle}>{displayName}</span>
        </MainMenu.ItemCustom>
      )}
      {onThemePreferenceChange && (
        <MainMenu.ItemCustom style={appearanceItemStyle}>
          <AppearanceSelector value={themePreference} onChange={onThemePreferenceChange} />
        </MainMenu.ItemCustom>
      )}
      {isLocalOnly && onGetFullAccess && (
        <MainMenu.Item onSelect={onGetFullAccess} icon={accessIcon}>
          Get full access
        </MainMenu.Item>
      )}

      {onSignOut && (
        <MainMenu.Item onSelect={onSignOut} icon={signOutIcon}>
          Sign out
        </MainMenu.Item>
      )}
    </MainMenu>
  );
}

function PreferencesMenu({
  getPreferenceState,
  onTogglePreference,
  editable,
}: {
  getPreferenceState: () => SupportedPreferenceState;
  onTogglePreference: (preference: SupportedPreference) => SupportedPreferenceState;
  editable: boolean;
}) {
  const [preferences, setPreferences] = useState(getPreferenceState);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState({ left: 0, top: 0 });
  const [darkTheme, setDarkTheme] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const focusOnOpenRef = useRef(false);

  const cancelClose = () => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    // The flyout is portaled with an 8px gap; allow crossing without flicker.
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      closeTimerRef.current = null;
    }, 250);
  };

  useEffect(
    () => () => {
      if (closeTimerRef.current !== null) clearTimeout(closeTimerRef.current);
    },
    [],
  );

  const toggle = (preference: SupportedPreference) => {
    setPreferences(onTogglePreference(preference));
  };

  const openMenu = () => {
    cancelClose();
    const trigger = triggerRef.current;
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const panelWidth = 296;
    const panelHeight = editable ? 246 : 168;
    const preferredLeft = rect.right + 8;
    setPosition({
      left:
        preferredLeft + panelWidth <= window.innerWidth - 8
          ? preferredLeft
          : Math.max(8, rect.left - panelWidth - 8),
      top: Math.min(Math.max(8, rect.bottom - panelHeight), window.innerHeight - panelHeight - 8),
    });
    setDarkTheme(trigger.closest(".excalidraw")?.classList.contains("theme--dark") ?? false);
    setPreferences(getPreferenceState());
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;

    if (focusOnOpenRef.current) {
      panelRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
      focusOnOpenRef.current = false;
    }
    const closeOnOutsideInteraction = (event: Event) => {
      const target = event.target as Node;
      if (!triggerRef.current?.contains(target) && !panelRef.current?.contains(target)) {
        setOpen(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    const closeOnViewportChange = () => setOpen(false);

    document.addEventListener("pointerdown", closeOnOutsideInteraction);
    document.addEventListener("touchstart", closeOnOutsideInteraction);
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("resize", closeOnViewportChange);
    window.addEventListener("scroll", closeOnViewportChange, true);
    return () => {
      document.removeEventListener("pointerdown", closeOnOutsideInteraction);
      document.removeEventListener("touchstart", closeOnOutsideInteraction);
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("resize", closeOnViewportChange);
      window.removeEventListener("scroll", closeOnViewportChange, true);
    };
  }, [open]);

  return (
    <MainMenu.ItemCustom style={preferencesContainerStyle}>
      <button
        ref={triggerRef}
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        className="dropdown-menu-item dropdown-menu-item-base"
        onMouseEnter={openMenu}
        onMouseLeave={scheduleClose}
        onClick={(event) => {
          event.stopPropagation();
          openMenu();
        }}
        onKeyDown={(event) => {
          if (event.key === "ArrowRight" || event.key === "ArrowDown") {
            event.preventDefault();
            event.stopPropagation();
            if (open) {
              panelRef.current?.querySelector<HTMLButtonElement>("button")?.focus();
            } else {
              focusOnOpenRef.current = true;
              openMenu();
            }
          }
        }}
        style={preferencesSummaryStyle}
      >
        <span style={preferencesSummaryLabelStyle}>
          {preferencesIcon}
          Preferences
        </span>
        <span aria-hidden="true">›</span>
      </button>
      {open &&
        createPortal(
          <div className={`excalidraw${darkTheme ? " theme--dark" : ""}`}>
            <div
              ref={panelRef}
              data-prevent-outside-click
              className="dropdown-menu"
              role="menu"
              aria-label="Preferences"
              onMouseEnter={cancelClose}
              onMouseLeave={scheduleClose}
              onFocus={cancelClose}
              onBlur={(event) => {
                if (
                  !event.currentTarget.contains(event.relatedTarget) &&
                  !triggerRef.current?.contains(event.relatedTarget)
                ) {
                  setOpen(false);
                }
              }}
              style={{
                ...preferencesPanelStyle,
                left: position.left,
                top: position.top,
              }}
            >
              {editable && (
                <>
                  <PreferenceItem
                    label="Tool lock"
                    shortcut="Q"
                    checked={preferences.toolLock}
                    onSelect={() => toggle("toolLock")}
                  />
                  <PreferenceItem
                    label="Snap to objects"
                    shortcut="Alt+S"
                    checked={preferences.objectsSnapMode}
                    onSelect={() => toggle("objectsSnapMode")}
                  />
                </>
              )}
              <PreferenceItem
                label="Toggle grid"
                shortcut="Ctrl+'"
                checked={preferences.gridMode}
                onSelect={() => toggle("gridMode")}
              />
              <PreferenceItem
                label="Zen mode"
                shortcut="Alt+Z"
                checked={preferences.zenMode}
                onSelect={() => toggle("zenMode")}
              />
              <PreferenceItem
                label="View mode"
                shortcut="Alt+R"
                checked={!editable || preferences.viewMode}
                disabled={!editable}
                onSelect={() => toggle("viewMode")}
              />
              <PreferenceItem
                label="Canvas & Shape properties"
                shortcut="Alt+/"
                checked={preferences.canvasProperties}
                onSelect={() => toggle("canvasProperties")}
              />
            </div>
          </div>,
          document.body,
        )}
    </MainMenu.ItemCustom>
  );
}

function PreferenceItem({
  label,
  shortcut,
  checked,
  onSelect,
  disabled = false,
}: {
  label: string;
  shortcut: string;
  checked: boolean;
  onSelect: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="menuitemcheckbox"
      className="dropdown-menu-item dropdown-menu-item-base"
      aria-label={label}
      aria-checked={checked}
      disabled={disabled}
      title={disabled ? "View mode is required by your read-only access." : undefined}
      onClick={(event) => {
        event.stopPropagation();
        onSelect();
      }}
      style={{
        ...preferenceItemStyle,
        ...(disabled ? { cursor: "not-allowed", opacity: 0.5 } : {}),
      }}
    >
      <span style={preferenceCheckStyle} aria-hidden="true">
        {checked ? checkIcon : null}
      </span>
      <span style={preferenceLabelStyle}>{label}</span>
      <span style={preferenceShortcutStyle} aria-hidden="true">
        {shortcut}
      </span>
    </button>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getInitials(name: string): string {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

// ---------------------------------------------------------------------------
// Inline SVG icons
// ---------------------------------------------------------------------------

const newDrawingIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="12" y1="18" x2="12" y2="12" />
    <line x1="9" y1="15" x2="15" y2="15" />
  </svg>
);

const accessIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="8" cy="15" r="5" />
    <path d="m12 11 8-8m-3 3 3 3m-6 0 3 3" />
  </svg>
);

const createAiIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="m3 18 12-12 3 3L6 21 3 18Z" />
    <path d="m12 9 3 3M20 2v4m-2-2h4M5 3v4M3 5h4M20 16v4m-2-2h4" />
  </svg>
);

const feedbackIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.6A8 8 0 1 1 21 12Z" />
  </svg>
);

const liveCollabIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="8.5" cy="7" r="4" />
    <path d="M20 8v6" />
    <path d="M23 11h-6" />
  </svg>
);

const preferencesIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="4" y1="6" x2="20" y2="6" />
    <circle cx="9" cy="6" r="2" />
    <line x1="4" y1="12" x2="20" y2="12" />
    <circle cx="15" cy="12" r="2" />
    <line x1="4" y1="18" x2="20" y2="18" />
    <circle cx="11" cy="18" r="2" />
  </svg>
);

const checkIcon = (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const saveIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
    <polyline points="17 21 17 13 7 13 7 21" />
    <polyline points="7 3 7 8 15 8" />
  </svg>
);

const signOutIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
    <polyline points="16 17 21 12 16 7" />
    <line x1="21" y1="12" x2="9" y2="12" />
  </svg>
);

const aboutIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const devToolsIcon = (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polyline points="16 18 22 12 16 6" />
    <polyline points="8 6 2 12 8 18" />
  </svg>
);

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const preferencesContainerStyle: React.CSSProperties = {
  marginTop: 0,
  padding: 0,
  position: "relative",
};

const preferencesSummaryStyle: React.CSSProperties = {
  justifyContent: "space-between",
  textAlign: "left",
};

const preferencesSummaryLabelStyle: React.CSSProperties = {
  alignItems: "center",
  display: "flex",
  gap: 10,
};

const preferencesPanelStyle: React.CSSProperties = {
  background: "var(--island-bg-color)",
  border: "1px solid var(--default-border-color)",
  borderRadius: 12,
  boxShadow: "var(--shadow-island)",
  display: "flex",
  flexDirection: "column",
  minWidth: 280,
  padding: 6,
  position: "fixed",
  zIndex: 10001,
};

const preferenceItemStyle: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "20px minmax(0, 1fr) auto",
  gap: 8,
  textAlign: "left",
};

const preferenceCheckStyle: React.CSSProperties = {
  alignItems: "center",
  display: "flex",
  justifyContent: "center",
};

const preferenceLabelStyle: React.CSSProperties = {
  minWidth: 0,
};

const preferenceShortcutStyle: React.CSSProperties = {
  color: "var(--keybinding-color)",
  fontSize: 12,
  whiteSpace: "nowrap",
};

const profileItemStyle: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 12px",
};

const appearanceItemStyle: React.CSSProperties = {
  margin: "1px",
  padding: "0 0.5rem",
  width: "calc(100% - 2px)",
  boxSizing: "border-box",
};

const menuAvatarStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: "50%",
  objectFit: "cover",
  flexShrink: 0,
};

const menuInitialsStyle: React.CSSProperties = {
  width: 28,
  height: 28,
  borderRadius: "50%",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  background: "var(--color-primary)",
  color: "var(--color-on-accent)",
  fontSize: 12,
  fontWeight: 600,
  flexShrink: 0,
};

const menuNameStyle: React.CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const liveCollabItemStyle: React.CSSProperties = {
  background: "var(--exms-success-soft)",
  color: "var(--exms-success-text)",
  borderRadius: 12,
};
